<script setup lang="ts">
import { computed, watch } from 'vue'
import { useVModel } from '@vueuse/core'

import BaseDrawer from './BaseDrawer.vue'
import { useVisibility } from '@/composables/visibility'

import type { ContestColor, GameContest } from './contestGame'

const props = defineProps<{
  modelValue: boolean
  boostStep: number
  contestGame: GameContest
  contestColor: ContestColor
}>()

const emit = defineEmits<{
  (e: 'onSubmit'): void
  (e: 'onSeeBoostDrawer'): void
}>()
const { isVisible } = useVisibility()
const isOpen = useVModel(props, 'modelValue', emit)

const boostTexts = computed(
  () =>
    props.contestGame.texts[
      props.contestGame.contestGameBoostType?.slug as keyof typeof props.contestGame.texts
    ],
)
const boostsIndex = ['redirection', 'condition', 'gift']

const closeModal = () => {
  isOpen.value = false
  emit('onSeeBoostDrawer')
}

watch(isVisible, (newVal) => {
  if (newVal) setTimeout(() => closeModal(), 5000)
})

const onClick = () => {
  emit('onSubmit')
}
</script>

<template>
  <BaseDrawer
    v-model="isOpen"
    :btn="{
      text: contestGame?.askActionButtonText,
      bgColor: contestColor?.buttonColor,
      textColor: contestColor?.buttonTextColor,
      isProcessing: false,
    }"
    :has-blur="false"
    :has-close-icon="false"
    :can-close="false"
    @on-btn-click="onClick"
    @update:model-value="isOpen = false"
  >
    <template #title>
      <p>{{ contestGame.askActionTitle }}</p>
    </template>
    <template #description>
      <div v-if="boostStep === 1 && contestGame.contestGameBoostType.slug === 'custom'">
        <p class="text-lg">{{ contestGame.askActionText }}</p>
      </div>
      <div v-else-if="boostStep === 1" class="w-full space-y-5">
        <div
          v-for="(boostIndex, index) in boostsIndex"
          :key="boostIndex"
          class="flex items-center rounded-xl bg-gray-100 p-3"
        >
          <div
            class="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-white text-center text-gray-800 shadow-xl md:h-8 md:w-8"
          >
            <p class="text-sm font-semibold text-gray-800">{{ index + 1 }}</p>
          </div>
          <div class="flex-1 text-left text-sm font-medium text-gray-800">
            <p>
              {{ boostTexts[boostIndex] }}
            </p>
          </div>
        </div>
      </div>
      <div v-else class="flex w-full flex-col items-center justify-center text-center">
        <img src="./assets/loading.gif" class="mx-auto -mt-5 h-24 w-24" />
        <p class="text-lg text-gray-700">{{ contestGame.waitingValidationText }}</p>
      </div>
    </template>
  </BaseDrawer>
</template>
